<template>
  <div class="contents">
    <div class="layout">

      <UserListHeader title="아티스트 회원 관리" menu="아티스트 회원 관리"/>

      <div class="white-panel mt20">
        <DataTable
          :columns="columns"
          :rows="items"
          row-key="uuid"
          :row-class="rowClass"
          searchable
          search-placeholder="ID 부분검색 · 이름·이메일·연락처는 정확히 입력"
          paged
          :page="page"
          :size="size"
          :total-elements="totalElements"
          :total-pages="totalPages"
          :sort-key="sortKey"
          :sort-direction="sortOrder"
          @update:page="onPage"
          @update:size="onSize"
          @sort="onSort"
          @search="onSearch"
        >
          <template #filter-roleType>
            <select v-model="selectedRole" @change="onFilter">
              <option value="">All</option>
              <option v-for="role in roleOptions" :key="role" :value="role">{{ role }}</option>
            </select>
          </template>

          <template #cell-no="{ index }">{{ index + 1 }}</template>

          <template #cell-name="{ row }">
            <router-link :to="{ name: 'UserArtistProfileDetail', params: { userAccountId: row.accountId } }">
              {{ row.name }}
            </router-link>
          </template>

          <template #cell-artwork="{ row }">
            <router-link :to="{ name: 'UserArtistArtWorkList', params: { userAccountId: row.accountId } }">
              아트워크
            </router-link>
          </template>

          <template #cell-accountId="{ row }">
            {{ row.accountId }}
            <span v-if="row.status === 'MIGRATED'"> → {{ row.migratedToAccountId }}</span>
            <a v-show="row.accountId"
               :href="`${platformUrl}/artist/${row.migratedToAccountId || row.accountId}`"
               target="_blank">🔗</a>
          </template>

          <template #cell-phoneNum="{ row }">{{ maskPhone(row.phoneNum) }}</template>

          <template #cell-action="{ row }">
            <template v-if="row.status === 'ACTIVE'">
              <button
                v-if="migrationSources.length === 0"
                @click="getMigrationSources"
                type="button"
                class="button green-button"
              >데이터 제공 가능한 계정 불러오기</button>
              <select
                v-else
                v-model="selectedSourceAccountId"
                @change="onSelectMigrationSource(row.name, row.accountId, selectedSourceAccountId)"
              >
                <option disabled value="">이관할 원본 계정을 선택하세요</option>
                <option v-for="(accountId, i) in migrationSources" :key="i" :value="accountId">
                  {{ accountId }}
                </option>
              </select>
            </template>
            <template v-else>
              <span v-if="row.status === 'DELETED'">삭제 처리 완료</span>
              <span v-else-if="row.status === 'MIGRATED'">데이터 이전 완료</span>
              <span v-else-if="row.status === 'WITHDRAWN'">
                탈퇴
                <button v-if="row.roleType === 'ARTIST'" @click="setUserStatusToDeleted(row.uuid)"
                        type="button" class="button red-button small">데이터 삭제</button>
              </span>
            </template>
          </template>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script setup>
import UserListHeader from './UserListHeader.vue';
import {onMounted, ref} from 'vue';
import {apiClient} from '@/services/auth-header';
import { useSort } from '@/composables/useSort';
import DataTable from '@/components/common/DataTable.vue';
import { maskPhone } from '@/common/mask';

const { sortKey, sortOrder, sortBy } = useSort('roleType', 'ASC');

// Platform URL (환경별로 다름)
const platformUrl = process.env.VUE_APP_PLATFORM_URL || process.env.VUE_APP_REDIRECT_URI || 'https://mimio.life';

const items = ref([]);
const selectedRole = ref('ARTIST');
const search = ref('');

const page = ref(0);
const size = ref(20);
const totalElements = ref(0);
const totalPages = ref(0);

const roleOptions = [
  'ARTIST_BEFORE_PROFILE',
  'ARTIST',
];

const columns = [
  { key: 'no', label: 'NO', minWidth: '50px' },
  { key: 'roleType', label: 'Role', sortable: true, minWidth: '210px' },
  { key: 'name', label: '이름(프로필)', minWidth: '110px' },
  { key: 'artwork', label: '아트워크', minWidth: '80px', align: 'center' },
  { key: 'accountId', label: 'ID(도메인연결)', minWidth: '220px' },
  { key: 'email', label: '이메일', minWidth: '210px' },
  { key: 'phoneNum', label: '전화번호', minWidth: '140px' },
  { key: 'action', label: '상태/데이터', minWidth: '300px' },
];

const rowClass = (row) => ({
  'bg-hidden': row.status !== 'ACTIVE',
  'line-through': row.status === 'DELETED',
});

const fetchItems = async () => {
  try {
    const response = await apiClient.get('/users/paged', {
      params: {
        roleTypes: selectedRole.value || ['ARTIST', 'ARTIST_BEFORE_PROFILE'],
        filter: search.value || undefined,
        page: page.value,
        size: size.value,
        sort: sortKey.value,
        direction: sortOrder.value,
      },
      paramsSerializer: params => {
        const sp = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') sp.append(k, v);
        });
        return sp.toString();
      },
    });
    const d = response.data;
    items.value = d.content ?? [];
    totalElements.value = d.totalElements ?? 0;
    totalPages.value = d.totalPages ?? 0;
  } catch (error) {
    console.error('Error:', error);
  }
};

const onPage = (p) => {
  page.value = p;
  fetchItems();
};
const onSize = (s) => {
  size.value = s;
  page.value = 0;
  fetchItems();
};
const onSort = (key) => {
  sortBy(key, () => {
    page.value = 0;
    fetchItems();
  });
};
const onFilter = () => {
  page.value = 0;
  fetchItems();
};
const onSearch = (keyword) => {
  search.value = keyword;
  page.value = 0;
  fetchItems();
};

const setUserStatusToDeleted = async (userUuid) => {
  if (!confirm('아티스트 도메인이 숨김처리됩니다.')) {
    return;
  }
  try {
    await apiClient.put(`/users/${userUuid}/status/deleted`);
    location.reload();
  } catch (error) {
    console.error('Error:', error);
  }
}

const migrationSources = ref([]); // 기존 targetUsers
const selectedSourceAccountId = ref('');

const getMigrationSources = async () => {
  try {
    const response = await apiClient.get('/users/migration-sources');
    migrationSources.value = response.data;
  } catch (error) {
    console.error('Error:', error);
  }
}

const onSelectMigrationSource = async (name, targetAccountId, sourceAccountId) => {
  const msg = `[${name}]님의 계정([${targetAccountId}])의 프로필 및 아트워크 데이터를 삭제하고,
선택한 계정([${sourceAccountId}])의 프로필 및 아트워크 데이터를 연결하시겠습니까?
연결 이후 이전 데이터는 복구할 수 없습니다.`;

  if (!confirm(msg)) return;

  await migrateArtistData(sourceAccountId, targetAccountId);
};

const migrateArtistData = async (sourceAccountId, targetAccountId) => {
  try {
    await apiClient.put(`/users/migrate/from/${sourceAccountId}/to/${targetAccountId}`);
    alert('데이터 이관이 완료되었습니다.');
    location.reload();
  } catch (error) {
    console.error('Migration error:', error);
    alert('이관 중 오류가 발생했습니다.');
  }
};

onMounted(async () => {
  await fetchItems();
});
</script>

